// src/pages/RegistrarLegalizcionAgendaViatico.jsx
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { normalizeImage } from "../utils/image";

const MODULO = "LegalizacionViaticos";
const MAX_FOTOGRAFIAS_DIA = 6;
const MAX_ASISTENCIAS_DIA = 3;
const MAX_ARCHIVO_MB = 8;

const SECCIONES_INFORME = [
  {
    id: "actividades",
    campo: "actividadesDesarrolladas",
    titulo: "Actividades desarrolladas",
    tituloCorto: "Actividades",
    descripcion:
      "Describe, de manera concreta, las actividades ejecutadas durante el desplazamiento.",
    ayuda:
      "Indica qué se hizo, dónde se realizó y cuál fue el propósito de la actividad.",
    icono: "fa-solid fa-list-check",
    placeholder:
      "Ejemplo: Se realizó acompañamiento técnico y verificación de las actividades programadas...",
    requerido: true,
  },
  {
    id: "resultados",
    campo: "resultados",
    titulo: "Resultados obtenidos",
    tituloCorto: "Resultados",
    descripcion:
      "Registra los productos, logros o resultados alcanzados durante la comisión.",
    ayuda:
      "Relaciona cada resultado con las actividades desarrolladas y evita repetir la actividad.",
    icono: "fa-solid fa-chart-line",
    placeholder:
      "Ejemplo: Se verificó el cumplimiento de la programación y se consolidó la información requerida...",
    requerido: true,
  },
  {
    id: "evidencias",
    campo: "evidencias",
    titulo: "Evidencias relacionadas",
    tituloCorto: "Evidencias",
    descripcion:
      "Describe las evidencias documentales, visuales o verificables que respaldan el informe.",
    ayuda:
      "Puedes mencionar actas, listados, registros fotográficos, correos, informes u otros soportes.",
    icono: "fa-solid fa-folder-open",
    placeholder:
      "Ejemplo: Registro fotográfico, listado de asistencia y acta de la reunión...",
    requerido: true,
  },
  {
    id: "compromisos",
    campo: "compromisos",
    titulo: "Compromisos",
    tituloCorto: "Compromisos",
    descripcion:
      "Registra las acciones posteriores acordadas durante el desplazamiento, cuando corresponda.",
    ayuda:
      "Este apartado es opcional. Registra un compromiso por cada acción pendiente.",
    icono: "fa-solid fa-handshake",
    placeholder:
      "Ejemplo: Remitir el informe complementario y compartir los documentos pendientes...",
    requerido: false,
  },
  {
    id: "conclusiones",
    campo: "conclusiones",
    titulo: "Conclusiones",
    tituloCorto: "Conclusiones",
    descripcion:
      "Resume los aspectos más importantes y el aporte del desplazamiento al cumplimiento del objetivo.",
    ayuda:
      "Redacta conclusiones claras, breves y relacionadas con los resultados obtenidos.",
    icono: "fa-solid fa-clipboard-check",
    placeholder:
      "Ejemplo: El desplazamiento permitió cumplir el objetivo previsto y fortalecer el seguimiento...",
    requerido: true,
  },
];

const normalizar = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

const obtenerModulo = (nombreModulo) => {
  try {
    const datos = JSON.parse(
      localStorage.getItem("permisos") ||
      localStorage.getItem("usuario") ||
      localStorage.getItem("user") ||
      localStorage.getItem("usuarios") ||
      "{}"
    );

    const modulos = Array.isArray(datos)
      ? datos
      : datos?.modulos ||
      datos?.permisos ||
      datos?.usuario?.modulos ||
      datos?.usuario?.permisos ||
      datos?.user?.modulos ||
      datos?.user?.permisos ||
      [];

    return (
      modulos.find((modulo) => {
        const nombre =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          modulo?.moduloObjectId?.nombreModulo ||
          "";

        return normalizar(nombre) === normalizar(nombreModulo);
      }) || {}
    );
  } catch (error) {
    console.error("Error leyendo permisos:", error);
    return {};
  }
};

const asegurarLista = (lista) =>
  Array.isArray(lista) && lista.length
    ? lista.map((item) => String(item || ""))
    : [""];

const limpiarLista = (lista = []) =>
  lista.map((item) => String(item || "").trim()).filter(Boolean);

const contarRegistros = (lista = []) =>
  lista.filter((item) => String(item || "").trim()).length;

const obtenerId = (valor) =>
  valor && typeof valor === "object" ? valor._id || "" : valor || "";

const fechaISO = (valor) => {
  if (!valor) return "";
  const fecha = String(valor).split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? fecha : "";
};

const formatearFecha = (valor) => {
  const fecha = fechaISO(valor);
  if (!fecha) return "—";
  const [year, month, day] = fecha.split("-");
  return `${day}/${month}/${year}`;
};

const formatearHora = (hora) => {
  if (!hora) return "—";

  const [hours, minutes] = String(hora).split(":");
  const fecha = new Date();
  fecha.setHours(Number(hours), Number(minutes), 0, 0);

  return fecha.toLocaleTimeString("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ImagenSoporte = ({ src, alt }) => {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div
        className="w-100 bg-body-tertiary text-secondary d-flex flex-column align-items-center justify-content-center"
        style={{ height: 150 }}
      >
        <i className="fa-solid fa-image-slash fs-3 mb-2"></i>
        <small>No se pudo mostrar la imagen</small>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-100"
      loading="lazy"
      onError={() => setError(true)}
      style={{ height: 150, objectFit: "cover" }}
    />
  );
};


const SelectorArchivos = ({
  tipo,
  diaId,
  disabled,
  onUpload,
}) => {
  const camaraRef = useRef(null);
  const galeriaRef = useRef(null);

  const seleccionarArchivos = async (event) => {
    const archivos = event.target.files;

    if (archivos?.length) {
      await onUpload(tipo, diaId, archivos);
    }

    event.target.value = "";
  };

  return (
    <>
      <div className="d-flex gap-2 selector-soportes mb-3">
        <button
          type="button"
          className="btn btn-dark boton-soporte"
          disabled={disabled}
          onClick={() => camaraRef.current?.click()}
          title="Tomar una fotografía"
        >
          <i className="fa-solid fa-camera"></i>
          <span>Cámara</span>
        </button>

        <button
          type="button"
          className="btn btn-outline-dark boton-soporte"
          disabled={disabled}
          onClick={() => galeriaRef.current?.click()}
          title="Seleccionar imágenes"
        >
          <i className="fa-solid fa-images"></i>
          <span>Galería</span>
        </button>
      </div>

      <input
        ref={camaraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="d-none"
        disabled={disabled}
        onChange={seleccionarArchivos}
      />

      <input
        ref={galeriaRef}
        type="file"
        accept="image/*"
        multiple
        className="d-none"
        disabled={disabled}
        onChange={seleccionarArchivos}
      />
    </>
  );
};

const RegistrarLegalizcionAgendaViatico = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditMode =
    location.pathname.includes("/editar/") ||
    location.pathname.includes("/update/");
  const isNewMode =
    location.pathname.includes("/nuevo/") ||
    location.pathname.includes("/registrar/");

  const [activeTab, setActiveTab] = useState("informacion");
  const [activeInformeSection, setActiveInformeSection] =
    useState("actividades");
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState("");
  const [progresoCarga, setProgresoCarga] = useState({});
  const [previsualizaciones, setPrevisualizaciones] = useState({});
  const previsualizacionesRef = useRef({});

  const [agenda, setAgenda] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [legalizacionId, setLegalizacionId] = useState("");
  const [soportesPorDia, setSoportesPorDia] = useState([]);

  const [formulario, setFormulario] = useState({
    ciudadPresentacion: "",
    fechaPresentacion: "",
    numeroOrdenViaje: "",
  });

  const [informe, setInforme] = useState({
    actividadesDesarrolladas: [""],
    resultados: [""],
    evidencias: [""],
    compromisos: [""],
    conclusiones: [""],
  });

  const modulo = obtenerModulo(MODULO);
  const puedeCrear =
    modulo?.puedeCrear === true ||
    modulo?.crear === true ||
    modulo?.create === true;
  const puedeActualizar =
    modulo?.puedeActualizar === true ||
    modulo?.actualizar === true ||
    modulo?.editar === true ||
    modulo?.update === true;

  const formularioHabilitado =
    (legalizacionId ? puedeActualizar : puedeCrear) &&
    !loading &&
    !guardando;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        ""
        }`,
    },
  };

  const stepProgress = {
    informacion: 33,
    informe: 66,
    soportes: 100,
  }[activeTab];

  const seccionesInforme = SECCIONES_INFORME.map((seccion) => ({
    ...seccion,
    valores: informe[seccion.campo],
  }));

  const seccionesPendientes = seccionesInforme.filter(
    (seccion) => seccion.requerido && contarRegistros(seccion.valores) === 0
  );
  const seccionesCompletas =
    seccionesInforme.length - seccionesPendientes.length;
  const totalRegistros = seccionesInforme.reduce(
    (total, seccion) => total + contarRegistros(seccion.valores),
    0
  );
  const resumenInforme = {
    seccionesPendientes,
    seccionesCompletas,
    totalRegistros,
    porcentaje: Math.round(
      (seccionesCompletas / seccionesInforme.length) * 100
    ),
  };

  const contratoActual =
    contrato ||
    (typeof agenda?.contrato === "object" ? agenda.contrato : {});
  const instructor = contratoActual?.instructor || {};

  const numeroContrato =
    contratoActual?.contrato ||
    contratoActual?.codigoContrato ||
    contratoActual?.numeroContrato ||
    contratoActual?.numeroContratoInstructor ||
    contratoActual?._id ||
    "—";

  const nombreContratista =
    contratoActual?.instructorNombreCompleto ||
    [
      instructor?.nombre1Instructor,
      instructor?.nombre2Instructor,
      instructor?.apellido1Instructor,
      instructor?.apellido2Instructor,
    ]
      .filter(Boolean)
      .join(" ") ||
    "—";

  const tipoDocumentoContratista =
    contratoActual?.tipoIdentificacion ||
    instructor?.tipodocumentoInstructor ||
    instructor?.tipoDocumentoInstructor ||
    "C.C.";

  const documentoContratista =
    instructor?.documentoInstructor ||
    contratoActual?.documentoInstructor ||
    "—";

  const validarSesion = (error) => {
    if (error?.response?.status !== 401) return false;

    Swal.fire({
      icon: "warning",
      title: "Sesión expirada",
      text: "Por favor inicia sesión nuevamente.",
      confirmButtonText: "Aceptar",
    }).then(() => {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      navigate("/", { replace: true });
    });

    return true;
  };

  const limpiarPrevisualizaciones = (claveCarga) => {
    (previsualizacionesRef.current[claveCarga] || []).forEach((item) => {
      if (item?.url?.startsWith("blob:")) URL.revokeObjectURL(item.url);
    });

    delete previsualizacionesRef.current[claveCarga];
    setPrevisualizaciones((actuales) => {
      const copia = { ...actuales };
      delete copia[claveCarga];
      return copia;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(previsualizacionesRef.current)
        .flat()
        .forEach((item) => {
          if (item?.url?.startsWith("blob:")) URL.revokeObjectURL(item.url);
        });
    };
  }, []);

  useEffect(() => {
    if (!id) return;

    const cargarDatos = async () => {
      try {
        setLoading(true);

        let agendaCargada = null;

        if (isEditMode) {
          const { data } = await axios.get(
            `/api/legalizacionesviaticos/listid/${id}`,
            config
          );

          if (!data?.ok || !data?.legalizacion) {
            throw new Error(data?.message || "No se pudo cargar la legalización.");
          }

          const legalizacion = data.legalizacion;
          const agendaRelacionada = legalizacion.agendaViaticoObjectId;

          setLegalizacionId(legalizacion._id || "");
          setFormulario({
            ciudadPresentacion: legalizacion.ciudadPresentacion || "",
            fechaPresentacion: fechaISO(legalizacion.fechaPresentacion),
            numeroOrdenViaje: legalizacion.numeroOrdenViaje || "",
          });
          setInforme({
            actividadesDesarrolladas: asegurarLista(
              legalizacion.actividadesDesarrolladas
            ),
            resultados: asegurarLista(legalizacion.resultados),
            evidencias: asegurarLista(legalizacion.evidencias),
            compromisos: asegurarLista(legalizacion.compromisos),
            conclusiones: asegurarLista(legalizacion.conclusiones),
          });
          setSoportesPorDia(
            Array.isArray(legalizacion.soportesPorDia)
              ? legalizacion.soportesPorDia
              : []
          );

          if (
            agendaRelacionada &&
            typeof agendaRelacionada === "object" &&
            Array.isArray(agendaRelacionada.agendaDias)
          ) {
            agendaCargada = agendaRelacionada;
          } else {
            const respuestaAgenda = await axios.get(
              `/api/agendasviaticos/listid/${obtenerId(agendaRelacionada)}`,
              config
            );
            agendaCargada = respuestaAgenda.data?.agenda;
          }
        } else {
          const { data } = await axios.get(
            `/api/agendasviaticos/listid/${id}`,
            config
          );
          agendaCargada = data?.agenda;
          setLegalizacionId("");
          setSoportesPorDia([]);
          setFormulario({
            ciudadPresentacion: "",
            fechaPresentacion: "",
            numeroOrdenViaje: "",
          });
          setInforme({
            actividadesDesarrolladas: [""],
            resultados: [""],
            evidencias: [""],
            compromisos: [""],
            conclusiones: [""],
          });
        }

        if (!agendaCargada?._id) {
          throw new Error("No se pudo cargar la agenda de viáticos.");
        }

        setAgenda(agendaCargada);

        const contratoId = obtenerId(agendaCargada.contrato);
        if (!contratoId) {
          setContrato(null);
          return;
        }

        const { data: datosContrato } = await axios.get(
          `/api/contratos/listid/${contratoId}`,
          config
        );

        if (!datosContrato?.ok) {
          throw new Error(
            datosContrato?.message ||
            "No se pudo cargar la información del contratista."
          );
        }

        setContrato(datosContrato.contrato || datosContrato.message || null);
      } catch (error) {
        console.error("Error cargando la información:", error);
        if (validarSesion(error)) return;

        Swal.fire(
          "Error",
          error?.response?.data?.message ||
          error.message ||
          "No se pudo cargar la información.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const limpiarFormulario = () => {
    setActiveTab("informacion");
    setActiveInformeSection("actividades");
    setFormulario({
      ciudadPresentacion: "",
      fechaPresentacion: "",
      numeroOrdenViaje: "",
    });
    setInforme({
      actividadesDesarrolladas: [""],
      resultados: [""],
      evidencias: [""],
      compromisos: [""],
      conclusiones: [""],
    });

    if (!legalizacionId) setSoportesPorDia([]);
  };

  const guardar = async (event) => {
    event.preventDefault();
    if (guardando) return;

    if (!agenda?._id) {
      Swal.fire("Error", "No se encontró la agenda asociada.", "error");
      return;
    }

    const esEdicion = Boolean(legalizacionId);
    if (esEdicion ? !puedeActualizar : !puedeCrear) {
      Swal.fire(
        "Sin permisos",
        esEdicion
          ? "No tiene permisos para actualizar legalizaciones."
          : "No tiene permisos para crear legalizaciones.",
        "warning"
      );
      return;
    }

    try {
      setGuardando(true);

      const payload = {
        agendaViaticoObjectId: agenda._id,
        ciudadPresentacion: formulario.ciudadPresentacion.trim(),
        fechaPresentacion: formulario.fechaPresentacion || null,
        numeroOrdenViaje: formulario.numeroOrdenViaje.trim(),
        actividadesDesarrolladas: limpiarLista(
          informe.actividadesDesarrolladas
        ),
        resultados: limpiarLista(informe.resultados),
        evidencias: limpiarLista(informe.evidencias),
        compromisos: limpiarLista(informe.compromisos),
        conclusiones: limpiarLista(informe.conclusiones),
      };

      const respuesta = esEdicion
        ? await axios.put(
          `/api/legalizacionesviaticos/update/${legalizacionId}`,
          payload,
          config
        )
        : await axios.post("/api/legalizacionesviaticos/add", payload, config);

      const data = respuesta.data;
      if (!data?.ok || !data?.legalizacion?._id) {
        throw new Error(data?.message || "No se pudo guardar la legalización.");
      }

      setLegalizacionId(data.legalizacion._id);
      if (Array.isArray(data.legalizacion.soportesPorDia)) {
        setSoportesPorDia(data.legalizacion.soportesPorDia);
      }

      await Swal.fire({
        icon: "success",
        title: esEdicion
          ? "Legalización actualizada"
          : "Legalización creada",
        text: data.message || "La información se guardó correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });

      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(
          `/instructor/legalizacionviaticos/listar/${agenda._id}`,
          { replace: true }
        );
      }
    } catch (error) {
      console.error("Error guardando la legalización:", error);
      if (validarSesion(error)) return;

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "No se pudo guardar la legalización.",
        "error"
      );
    } finally {
      setGuardando(false);
    }
  };

  const cambiarPestana = (pestana) => {
    if (pestana === "soportes" && !legalizacionId) {
      Swal.fire({
        icon: "info",
        title: "Guarda primero la legalización",
        text: "Debes guardar la legalización antes de cargar fotografías y asistencias.",
      });
      return;
    }

    setActiveTab(pestana);
  };

  const actualizarRegistro = (campo, index, valor) => {
    setInforme((actual) => ({
      ...actual,
      [campo]: actual[campo].map((item, posicion) =>
        posicion === index ? valor : item
      ),
    }));
  };

  const agregarRegistro = (campo) => {
    setInforme((actual) => ({
      ...actual,
      [campo]: [...actual[campo], ""],
    }));
  };

  const eliminarRegistro = (campo, index) => {
    setInforme((actual) => {
      const lista = actual[campo].filter((_, posicion) => posicion !== index);
      return { ...actual, [campo]: lista.length ? lista : [""] };
    });
  };

  const buscarSoporteDia = (agendaDiaId) =>
    soportesPorDia.find(
      (soporte) =>
        String(obtenerId(soporte.agendaDiaObjectId)) === String(agendaDiaId)
    );

  const actualizarSoporteDia = (nuevoSoporte) => {
    const agendaDiaId = obtenerId(nuevoSoporte?.agendaDiaObjectId);
    if (!agendaDiaId) return;

    setSoportesPorDia((actuales) => {
      const existe = actuales.some(
        (soporte) =>
          String(obtenerId(soporte.agendaDiaObjectId)) === String(agendaDiaId)
      );

      return existe
        ? actuales.map((soporte) =>
          String(obtenerId(soporte.agendaDiaObjectId)) ===
            String(agendaDiaId)
            ? nuevoSoporte
            : soporte
        )
        : [...actuales, nuevoSoporte];
    });
  };

  const validarArchivos = (archivos, cantidadActual, maximo) => {
    const lista = Array.from(archivos || []);

    if (!lista.length) return { error: "No seleccionaste archivos." };
    if (cantidadActual + lista.length > maximo) {
      return { error: `Solo se permiten máximo ${maximo} imágenes por día.` };
    }

    const archivoInvalido = lista.find(
      (archivo) =>
        !/image\//i.test(archivo.type || "") &&
        !/\.(jpeg|jpg|png|svg|webp|heic|heif)$/i.test(archivo.name || "")
    );
    if (archivoInvalido) {
      return {
        error:
          "Solo se permiten imágenes JPG, JPEG, PNG, SVG, WEBP, HEIC o HEIF.",
      };
    }

    if (
      lista.some(
        (archivo) => archivo.size > MAX_ARCHIVO_MB * 1024 * 1024
      )
    ) {
      return { error: `Cada imagen debe pesar máximo ${MAX_ARCHIVO_MB} MB.` };
    }

    return { archivos: lista };
  };

  const subirArchivos = async (tipo, agendaDiaId, archivosSeleccionados) => {
    if (!legalizacionId) {
      Swal.fire("Atención", "Guarda primero la legalización.", "warning");
      return;
    }

    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tienes permisos para modificar los soportes de esta legalización.",
        "warning"
      );
      return;
    }

    const esFotografico = tipo === "registroFotografico";
    const soporte = buscarSoporteDia(agendaDiaId);
    const listaActual = esFotografico
      ? soporte?.registroFotografico || []
      : soporte?.registroAsistencia || [];
    const maximo = esFotografico
      ? MAX_FOTOGRAFIAS_DIA
      : MAX_ASISTENCIAS_DIA;
    const validacion = validarArchivos(
      archivosSeleccionados,
      listaActual.length,
      maximo
    );

    if (validacion.error) {
      Swal.fire("Atención", validacion.error, "warning");
      return;
    }

    const claveCarga = `${tipo}-${agendaDiaId}`;

    try {
      setSubiendo(claveCarga);
      setProgresoCarga((actual) => ({ ...actual, [claveCarga]: 0 }));

      const archivos = await Promise.all(
        validacion.archivos.map((archivo) => normalizeImage(archivo))
      );

      limpiarPrevisualizaciones(claveCarga);

      const vistasPrevias = archivos.map((archivo, index) => ({
        id: `${claveCarga}-${Date.now()}-${index}`,
        nombre: archivo.name || `imagen-${index + 1}.jpg`,
        url: URL.createObjectURL(archivo),
      }));

      previsualizacionesRef.current[claveCarga] = vistasPrevias;
      setPrevisualizaciones((actual) => ({
        ...actual,
        [claveCarga]: vistasPrevias,
      }));

      const formData = new FormData();
      archivos.forEach((archivo) => formData.append(tipo, archivo));

      const endpoint = esFotografico
        ? `/api/legalizacionesviaticos/registro-fotografico/${legalizacionId}/${agendaDiaId}`
        : `/api/legalizacionesviaticos/registro-asistencia/${legalizacionId}/${agendaDiaId}`;

      const { data } = await axios.post(endpoint, formData, {
        ...config,
        onUploadProgress: ({ loaded, total }) => {
          const porcentaje = total ? Math.round((loaded * 100) / total) : 0;
          setProgresoCarga((actual) => ({
            ...actual,
            [claveCarga]: porcentaje,
          }));
        },
      });

      if (!data?.ok || !data?.soporteDia) {
        throw new Error(data?.message || "No se pudieron cargar las imágenes.");
      }

      actualizarSoporteDia(data.soporteDia);
      await Swal.fire({
        icon: "success",
        title: data.message || "Imágenes cargadas",
        showConfirmButton: false,
        timer: 1300,
      });
    } catch (error) {
      console.error("Error subiendo soportes:", error);
      if (validarSesion(error)) return;

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "No se pudieron cargar las imágenes.",
        "error"
      );
    } finally {
      limpiarPrevisualizaciones(claveCarga);
      setProgresoCarga((actual) => {
        const copia = { ...actual };
        delete copia[claveCarga];
        return copia;
      });
      setSubiendo("");
    }
  };

  const eliminarImagen = async ({ tipo, agendaDiaObjectId, imagenId }) => {
    if (!legalizacionId || !imagenId) return;

    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tienes permisos para eliminar soportes de esta legalización.",
        "warning"
      );
      return;
    }

    const confirmacion = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar imagen?",
      text: "La imagen será eliminada permanentemente.",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) return;

    const esFotografico = tipo === "registroFotografico";
    const campo = esFotografico
      ? "registroFotografico"
      : "registroAsistencia";
    const claveCarga = `eliminar-${imagenId}`;
    const endpoint = esFotografico
      ? `/api/legalizacionesviaticos/registro-fotografico/${legalizacionId}/${agendaDiaObjectId}/${imagenId}`
      : `/api/legalizacionesviaticos/registro-asistencia/${legalizacionId}/${agendaDiaObjectId}/${imagenId}`;

    try {
      setSubiendo(claveCarga);
      const { data } = await axios.delete(endpoint, config);

      if (!data?.ok) {
        throw new Error(data?.message || "No se pudo eliminar la imagen.");
      }

      setSoportesPorDia((actuales) =>
        actuales.map((soporte) =>
          String(obtenerId(soporte.agendaDiaObjectId)) ===
            String(agendaDiaObjectId)
            ? {
              ...soporte,
              [campo]: (soporte[campo] || []).filter(
                (imagen) => String(imagen._id) !== String(imagenId)
              ),
            }
            : soporte
        )
      );

      await Swal.fire({
        icon: "success",
        title: data.message || "Imagen eliminada",
        showConfirmButton: false,
        timer: 1200,
      });
    } catch (error) {
      console.error("Error eliminando imagen:", error);
      if (validarSesion(error)) return;

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "No se pudo eliminar la imagen.",
        "error"
      );
    } finally {
      setSubiendo("");
    }
  };

  const renderInformeModerno = () => {
    const seccionActiva =
      seccionesInforme.find(
        (seccion) =>
          seccion.id === activeInformeSection
      ) || seccionesInforme[0];

    const indiceActivo = seccionesInforme.findIndex(
      (seccion) =>
        seccion.id === seccionActiva.id
    );

    const irSeccionAnterior = () => {
      if (indiceActivo <= 0) return;

      setActiveInformeSection(
        seccionesInforme[indiceActivo - 1].id
      );
    };

    const irSeccionSiguiente = () => {
      if (
        indiceActivo >=
        seccionesInforme.length - 1
      ) {
        cambiarPestana("soportes");
        return;
      }

      setActiveInformeSection(
        seccionesInforme[indiceActivo + 1].id
      );
    };

    const registrosActivos =
      contarRegistros(
        seccionActiva.valores
      );

    return (
      <div>
        <div className="border rounded-4 overflow-hidden mb-4">
          <div className="p-3 p-md-4 border-bottom bg-body-tertiary">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h6 className="fw-semibold mb-1">
                  <i className="fa-solid fa-file-circle-check me-2"></i>
                  Informe de legalización
                </h6>

                <small className="text-secondary">
                  Diligencia cada apartado por separado. La información
                  se guardará como una lista de registros independientes.
                </small>
              </div>

              <span className="badge text-bg-dark rounded-pill px-3 py-2">
                {resumenInforme.porcentaje}% diligenciado
              </span>
            </div>

            <div className="progress mt-3" style={{ height: 8 }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${resumenInforme.porcentaje}%`,
                }}
                aria-valuenow={resumenInforme.porcentaje}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>

            <div className="row g-2 mt-2">
              <div className="col-12 col-sm-4">
                <div className="bg-white border rounded-3 p-3 h-100">
                  <div className="small text-secondary">
                    Secciones completas
                  </div>

                  <div className="fw-bold fs-5">
                    {resumenInforme.seccionesCompletas}/
                    {seccionesInforme.length}
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-4">
                <div className="bg-white border rounded-3 p-3 h-100">
                  <div className="small text-secondary">
                    Registros redactados
                  </div>

                  <div className="fw-bold fs-5">
                    {resumenInforme.totalRegistros}
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-4">
                <div className="bg-white border rounded-3 p-3 h-100">
                  <div className="small text-secondary">
                    Apartados pendientes
                  </div>

                  <div className="fw-bold fs-5">
                    {
                      resumenInforme
                        .seccionesPendientes
                        .length
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-0">
            <div className="col-12 col-lg-4 col-xl-3 border-end">
              <div className="p-3 p-md-4">
                <div className="small fw-semibold text-secondary text-uppercase mb-3">
                  Apartados del informe
                </div>

                <div className="d-flex flex-column gap-2">
                  {seccionesInforme.map(
                    (seccion, index) => {
                      const cantidad =
                        contarRegistros(
                          seccion.valores
                        );

                      const completa =
                        !seccion.requerido ||
                        cantidad > 0;

                      const activa =
                        seccion.id ===
                        seccionActiva.id;

                      return (
                        <button
                          key={seccion.id}
                          type="button"
                          className={`btn text-start border rounded-3 p-3 ${activa
                            ? "btn-dark"
                            : "btn-light"
                            }`}
                          onClick={() =>
                            setActiveInformeSection(
                              seccion.id
                            )
                          }
                          disabled={
                            loading || guardando
                          }
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${activa
                                ? "bg-white text-dark"
                                : "bg-body-secondary text-dark"
                                }`}
                              style={{
                                width: 38,
                                height: 38,
                              }}
                            >
                              <i
                                className={
                                  seccion.icono
                                }
                              ></i>
                            </div>

                            <div className="flex-grow-1 overflow-hidden">
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                <span className="fw-semibold text-truncate">
                                  {index + 1}.{" "}
                                  {
                                    seccion.tituloCorto
                                  }
                                </span>

                                <span
                                  className={`badge ${activa
                                    ? "text-bg-light"
                                    : completa
                                      ? "text-bg-success"
                                      : "text-bg-secondary"
                                    }`}
                                >
                                  {cantidad}
                                </span>
                              </div>

                              <div
                                className={`small mt-1 ${activa
                                  ? "text-white-50"
                                  : "text-secondary"
                                  }`}
                              >
                                {seccion.requerido
                                  ? completa
                                    ? "Completado"
                                    : "Obligatorio"
                                  : cantidad > 0
                                    ? "Con registros"
                                    : "Opcional"}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    }
                  )}
                </div>

                {resumenInforme
                  .seccionesPendientes.length >
                  0 && (
                    <div className="alert alert-warning small mt-3 mb-0">
                      <i className="fa-solid fa-circle-exclamation me-2"></i>
                      Aún faltan apartados obligatorios para enviar la
                      legalización a revisión.
                    </div>
                  )}
              </div>
            </div>

            <div className="col-12 col-lg-8 col-xl-9">
              <div className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 pb-3 border-bottom">
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="rounded-circle bg-body-tertiary border d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        fontSize: 19,
                      }}
                    >
                      <i
                        className={
                          seccionActiva.icono
                        }
                      ></i>
                    </div>

                    <div>
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <h6 className="fw-semibold mb-0">
                          {seccionActiva.titulo}
                        </h6>

                        {seccionActiva.requerido ? (
                          <span className="badge text-bg-danger">
                            Obligatorio
                          </span>
                        ) : (
                          <span className="badge text-bg-secondary">
                            Opcional
                          </span>
                        )}
                      </div>

                      <div className="small text-secondary mt-2">
                        {seccionActiva.descripcion}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-dark d-flex align-items-center justify-content-center"
                    onClick={() => agregarRegistro(seccionActiva.campo)}
                    disabled={!formularioHabilitado}
                    title="Agregar un nuevo registro"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Agregar registro
                  </button>
                </div>

                <div className="alert alert-light border small mt-3">
                  <i className="fa-regular fa-lightbulb me-2"></i>
                  <strong>Sugerencia:</strong>{" "}
                  {seccionActiva.ayuda}
                </div>

                <div className="d-flex flex-column gap-3 mt-4">
                  {seccionActiva.valores.map(
                    (valor, index) => (
                      <div
                        className="border rounded-4 overflow-hidden"
                        key={`${seccionActiva.id}-${index}`}
                      >
                        <div className="d-flex justify-content-between align-items-center gap-3 px-3 py-2 bg-body-tertiary border-bottom">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="badge text-bg-dark rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                width: 28,
                                height: 28,
                              }}
                            >
                              {index + 1}
                            </span>

                            <span className="small fw-semibold">
                              Registro {index + 1}
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-3">
                            <span className="small text-secondary">
                              {
                                String(
                                  valor || ""
                                ).length
                              }{" "}
                              caracteres
                            </span>

                            {seccionActiva.valores.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center"
                                onClick={() =>
                                  eliminarRegistro(seccionActiva.campo, index)
                                }
                                disabled={!formularioHabilitado}
                                title="Eliminar registro"
                              >
                                <i className="fa-solid fa-trash"></i>
                                <span className="d-none d-sm-inline ms-2">
                                  Eliminar
                                </span>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="p-3">
                          <textarea
                            className="form-control border-0 shadow-none p-0"
                            rows={1}
                            value={valor}
                            maxLength={2000}
                            onChange={(event) =>
                              actualizarRegistro(
                                seccionActiva.campo,
                                index,
                                event.target.value
                              )
                            }
                            placeholder={
                              seccionActiva.placeholder
                            }
                            disabled={
                              !formularioHabilitado
                            }
                            style={{
                              resize: "vertical",
                              minHeight: 20,
                            }}
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4 pt-3 border-top">
                  <div className="small text-secondary">
                    <strong>
                      {registrosActivos}
                    </strong>{" "}
                    registro
                    {registrosActivos === 1
                      ? ""
                      : "s"}{" "}
                    diligenciado
                    {registrosActivos === 1
                      ? ""
                      : "s"}{" "}
                    en este apartado.
                  </div>

                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-dark flex-grow-1 flex-sm-grow-0"
                      onClick={irSeccionAnterior}
                      disabled={
                        indiceActivo === 0 ||
                        loading ||
                        guardando
                      }
                      title="Ir al apartado anterior"
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i>
                      Anterior
                    </button>

                    <button
                      type="button"
                      className="btn btn-dark flex-grow-1 flex-sm-grow-0"
                      onClick={irSeccionSiguiente}
                      disabled={loading || guardando}
                      title={
                        indiceActivo === seccionesInforme.length - 1
                          ? "Continuar a soportes"
                          : "Ir al siguiente apartado"
                      }
                    >
                      <span className="d-none d-sm-inline">
                        {indiceActivo === seccionesInforme.length - 1
                          ? "Continuar a soportes"
                          : "Siguiente apartado"}
                      </span>
                      <span className="d-sm-none">
                        {indiceActivo === seccionesInforme.length - 1
                          ? "Soportes"
                          : "Siguiente"}
                      </span>
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-2 justify-content-between">
          <div className="col-6 col-md-auto">
            <button
              type="button"
              className="btn btn-outline-dark w-100"
              onClick={() => setActiveTab("informacion")}
              disabled={loading || guardando}
              title="Volver a la información general"
            >
              <i className="fa-solid fa-arrow-left me-2"></i>
              <span className="d-none d-md-inline">Volver a información</span>
              <span className="d-md-none">Información</span>
            </button>
          </div>

          <div className="col-6 col-md-auto">
            <button
              type="button"
              className="btn btn-dark w-100"
              onClick={() => cambiarPestana("soportes")}
              disabled={loading || guardando}
              title="Continuar a soportes"
            >
              <span className="d-none d-md-inline">Continuar a soportes</span>
              <span className="d-md-none">Soportes</span>
              <i className="fa-solid fa-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderGaleria = ({
    imagenes,
    tipo,
    agendaDiaObjectId,
  }) => {
    const esFotografico =
      tipo === "registroFotografico";

    const claveCarga =
      `${tipo}-${agendaDiaObjectId}`;

    const vistasPrevias =
      previsualizaciones[
      claveCarga
      ] || [];

    const porcentaje =
      progresoCarga[
      claveCarga
      ] || 0;

    if (
      !imagenes?.length &&
      vistasPrevias.length === 0
    ) {
      return (
        <div className="text-center text-secondary border rounded-3 py-4 px-3 bg-body-tertiary">
          <i
            className={`fa-solid ${esFotografico
              ? "fa-camera"
              : "fa-file-signature"
              } fs-3 mb-2`}
          ></i>

          <div className="small">
            No hay imágenes cargadas.
          </div>
        </div>
      );
    }

    return (
      <div className="row g-2">
        {vistasPrevias.map(
          (vistaPrevia) => (
            <div
              className="col-6 col-md-4 col-lg-3"
              key={vistaPrevia.id}
            >
              <div className="border rounded-3 overflow-hidden bg-white h-100">
                <img
                  src={vistaPrevia.url}
                  alt="Vista previa"
                  className="w-100"
                  style={{
                    height: 150,
                    objectFit: "cover",
                  }}
                />

                <div className="p-2">
                  <div
                    className="small text-secondary text-truncate mb-2"
                    title={
                      vistaPrevia.nombre
                    }
                  >
                    {
                      vistaPrevia.nombre
                    }
                  </div>

                  <div className="progress mb-2" style={{ height: 8 }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${porcentaje}%`,
                      }}
                      aria-valuenow={porcentaje}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>

                  <div className="small text-center text-secondary">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Subiendo {porcentaje}%
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {(imagenes || []).map((imagen) => {
  const imagenUrl = esFotografico
    ? imagen.imagenRegistroFotografico
    : imagen.imagenRegistroAsistencia;

  return (
    <div
      className="col-6 col-sm-4 col-md-3 col-lg-2 d-flex justify-content-center"
      key={imagen._id}
      style={{ minWidth: 0 }}
    >
      <div
        className="border rounded-3 overflow-hidden bg-white w-100 shadow-sm"
        style={{
          maxWidth: 170,
          minWidth: 0,
        }}
      >
        <a
          href={imagenUrl || undefined}
          target="_blank"
          rel="noreferrer"
          title="Abrir imagen"
          className="d-block overflow-hidden"
          style={{
            width: "100%",
            aspectRatio: "4 / 3",
          }}
        >
          <ImagenSoporte
            src={imagenUrl}
            alt={
              esFotografico
                ? "Registro fotográfico"
                : "Registro de asistencia"
            }
          />
        </a>

        <div className="p-2">
          <button
            type="button"
            className="btn btn-outline-danger btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
            disabled={
              !formularioHabilitado ||
              subiendo === `eliminar-${imagen._id}`
            }
            onClick={() =>
              eliminarImagen({
                tipo,
                agendaDiaObjectId,
                imagenId: imagen._id,
              })
            }
            title="Eliminar imagen"
          >
            <i
              className={`fa-solid ${
                subiendo === `eliminar-${imagen._id}`
                  ? "fa-spinner fa-spin"
                  : "fa-trash"
              }`}
            ></i>

            <span className="d-none d-sm-inline">
              Eliminar
            </span>
          </button>
        </div>
      </div>
    </div>
  );
})}
      </div>
    );
  };

  if (isNewMode && !puedeCrear) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para crear legalizaciones de viáticos.
        </div>

        <button
          className="btn btn-outline-dark"
          type="button"
          onClick={() => navigate(-1)}
        >
          <i className="fa-solid fa-arrow-left me-2"></i>
          Volver
        </button>
      </div>
    );
  }

  if (isEditMode && !puedeActualizar) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para actualizar legalizaciones de viáticos.
        </div>

        <button
          className="btn btn-outline-dark"
          type="button"
          onClick={() => navigate(-1)}
        >
          <i className="fa-solid fa-arrow-left me-2"></i>
          Volver
        </button>
      </div>
    );
  }

  return (
   
      <div>
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {legalizacionId
                    ? "Editar Legalización de Viáticos"
                    : "Nueva Legalización de Viáticos"}
                </h5>

                <small className="text-secondary">
                  Registra el informe y los soportes asociados a la agenda
                  de desplazamiento.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  disabled={loading || guardando}
                  onClick={() => navigate(-1)}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={!formularioHabilitado}
                  onClick={limpiarFormulario}
                  title="Limpiar formulario"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="submit"
                  form="form-legalizacion-viaticos"
                  className="btn btn-dark"
                  disabled={!formularioHabilitado}
                  title={
                    legalizacionId
                      ? "Actualizar legalización"
                      : "Guardar legalización"
                  }
                >
                  <i
                    className={`fa-solid ${guardando
                      ? "fa-spinner fa-spin"
                      : "fa-floppy-disk"
                      } me-2`}
                  ></i>

                  {legalizacionId ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className={
                    activeTab === "informacion"
                      ? "btn btn-dark"
                      : "btn btn-outline-dark"
                  }
                  disabled={loading || guardando}
                  onClick={() => cambiarPestana("informacion")}
                  title="Información"
                >
                  <i className="fa-solid fa-circle-info me-2"></i>
                  Información
                </button>

                <button
                  type="button"
                  className={
                    activeTab === "informe"
                      ? "btn btn-dark"
                      : "btn btn-outline-dark"
                  }
                  disabled={loading || guardando}
                  onClick={() => cambiarPestana("informe")}
                  title="Informe"
                >
                  <i className="fa-solid fa-file-lines me-2"></i>
                  Informe
                </button>

                <button
                  type="button"
                  className={
                    activeTab === "soportes"
                      ? "btn btn-dark"
                      : "btn btn-outline-dark"
                  }
                  disabled={loading || guardando}
                  onClick={() => cambiarPestana("soportes")}
                  title="Soportes"
                >
                  <i className="fa-solid fa-images me-2"></i>
                  Soportes
                </button>
              </div>

              <div className="progress mt-2" style={{ height: 8 }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${stepProgress}%` }}
                  aria-valuenow={stepProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                ></div>

                <div className="mt-3">
                  Cargando información…
                </div>
              </div>
            ) : !agenda ? (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-3">
                  <i className="fa-solid fa-calendar-xmark"></i>
                </div>

                <div className="fw-semibold">
                  No se encontró la agenda
                </div>

                <div className="small">
                  Verifica el identificador recibido en la ruta.
                </div>
              </div>
            ) : (
              <form
                id="form-legalizacion-viaticos"
                onSubmit={guardar}
                noValidate
              >
                {activeTab === "informacion" && (
                  <div>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 border-bottom pb-3 mb-4">
                      <div>
                        <h6 className="fw-semibold mb-1">
                          <i className="fa-solid fa-calendar-check me-2"></i>
                          Agenda asociada
                        </h6>

                        <small className="text-secondary">
                          La agenda se consulta automáticamente y no se
                          modifica desde este formulario.
                        </small>
                      </div>

                      <span className="badge text-bg-dark px-3 py-2">
                        Contrato: {numeroContrato}
                      </span>
                    </div>

                    <div className="border bg-body-tertiary rounded-3 p-3 mb-4">
                      <div className="row align-items-center g-3">
                        {/* ÍCONO */}
                        <div className="col-auto">
                          <div
                            className="rounded-circle bg-dark text-white d-flex align-items-center justify-content-center"
                            style={{
                              width: 44,
                              height: 44,
                            }}
                          >
                            <i className="fa-solid fa-user"></i>
                          </div>
                        </div>

                        {/* NOMBRE */}
                        <div className="col-12 col-md">
                          <div className="small text-secondary">
                            Nombre del contratista
                          </div>

                          <div className="fw-semibold text-uppercase text-break">
                            {nombreContratista}
                          </div>
                        </div>

                        {/* TIPO DE DOCUMENTO */}
                        <div className="col-5 col-md-2">
                          <div className="small text-secondary">
                            Tipo
                          </div>

                          <div className="fw-semibold">
                            {tipoDocumentoContratista}
                          </div>
                        </div>

                        {/* DOCUMENTO */}
                        <div className="col-7 col-md-3">
                          <div className="small text-secondary">
                            Documento
                          </div>

                          <div className="fw-semibold text-break">
                            {documentoContratista}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-12 col-lg-6">
                        <div className="border rounded-4 p-3 p-md-4 h-100">
                          <div className="small text-secondary">
                            Entidad
                          </div>

                          <div className="fw-semibold text-break">
                            {agenda.entidad || "—"}
                          </div>

                          <div className="small text-secondary mt-3">
                            Ruta
                          </div>

                          <div className="fw-semibold text-break">
                            {agenda.ruta || "—"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6">
                        <div className="border rounded-4 p-3 p-md-4 h-100">
                          <div className="row g-3">
                            <div className="col-6">
                              <div className="small text-secondary">
                                Fecha inicial
                              </div>

                              <div className="fw-semibold">
                                {formatearFecha(
                                  agenda.fechaInicioDesplazamiento
                                )}
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="small text-secondary">
                                Fecha final
                              </div>

                              <div className="fw-semibold">
                                {formatearFecha(
                                  agenda.fechaFinDesplazamiento
                                )}
                              </div>
                            </div>

                            <div className="col-12">
                              <div className="small text-secondary">
                                Ciudad / Municipio / Departamento
                              </div>

                              <div className="fw-semibold text-break">
                                {agenda.ciudadDepartamentoMunicipio ||
                                  "—"}
                              </div>
                            </div>

                            <div className="col-12">
                              <div className="small text-secondary">
                                Objetivo del desplazamiento
                              </div>

                              <div className="text-break">
                                {agenda.objetivoDesplazamiento || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded-4 p-3 p-md-4 bg-body-tertiary">
                      <div className="row g-3">
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold">
                            Ciudad de presentación
                          </label>

                          <input
                            type="text"
                            className="form-control"
                            value={formulario.ciudadPresentacion}
                            onChange={(event) =>
                              setFormulario((actual) => ({
                                ...actual,
                                ciudadPresentacion: event.target.value,
                              }))
                            }
                            placeholder="Ej: Medellín"
                            disabled={!formularioHabilitado}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold">
                            Fecha de presentación
                          </label>

                          <input
                            type="date"
                            className="form-control"
                            value={formulario.fechaPresentacion}
                            onChange={(event) =>
                              setFormulario((actual) => ({
                                ...actual,
                                fechaPresentacion: event.target.value,
                              }))
                            }
                            disabled={!formularioHabilitado}
                          />
                        </div>

                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold">
                            Número de orden de viaje
                          </label>

                          <input
                            type="text"
                            className="form-control"
                            value={formulario.numeroOrdenViaje}
                            onChange={(event) =>
                              setFormulario((actual) => ({
                                ...actual,
                                numeroOrdenViaje: event.target.value,
                              }))
                            }
                            placeholder="Número de la orden"
                            disabled={!formularioHabilitado}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="d-grid d-md-flex justify-content-md-end mt-4">
                      <button
                        type="button"
                        className="btn btn-dark"
                        onClick={() =>
                          cambiarPestana("informe")
                        }
                        disabled={loading || guardando}
                      >
                        Continuar al informe
                        <i className="fa-solid fa-arrow-right ms-2"></i>
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "informe" &&
                  renderInformeModerno()}

                {activeTab === "soportes" && (
                  <div>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                      <div>
                        <h6 className="fw-semibold mb-1">
                          <i className="fa-solid fa-images me-2"></i>
                          Soportes por día
                        </h6>

                        <small className="text-secondary">
                          Cada día requiere mínimo una fotografía y una
                          imagen del registro de asistencia.
                        </small>
                      </div>
                    </div>

                    {(agenda.agendaDias || []).length === 0 ? (
                      <div className="alert alert-warning">
                        La agenda no tiene días registrados.
                      </div>
                    ) : (
                      (agenda.agendaDias || []).map(
                        (dia, index) => {
                          const soporte =
                            buscarSoporteDia(dia._id) || {
                              registroFotografico: [],
                              registroAsistencia: [],
                            };

                          const fotos =
                            soporte.registroFotografico || [];

                          const asistencias =
                            soporte.registroAsistencia || [];

                          const claveFotos = `registroFotografico-${dia._id}`;
                          const claveAsistencia = `registroAsistencia-${dia._id}`;

                          const bloquesSoporte = [
                            {
                              tipo: "registroFotografico",
                              titulo: "Registro fotográfico",
                              imagenes: fotos,
                              maximo: MAX_FOTOGRAFIAS_DIA,
                              claveCarga: claveFotos,
                              ayuda:
                                "En el celular puedes tomar una foto y cargarla de inmediato, o seleccionar varias imágenes desde la galería.",
                              cargando: "Cargando fotografías…",
                            },
                            {
                              tipo: "registroAsistencia",
                              titulo: "Registro de asistencia",
                              imagenes: asistencias,
                              maximo: MAX_ASISTENCIAS_DIA,
                              claveCarga: claveAsistencia,
                              ayuda:
                                "Puedes fotografiar cada hoja de asistencia o seleccionar varias imágenes guardadas en el celular.",
                              cargando: "Cargando asistencias…",
                            },
                          ];

                          return (
                            <div key={dia._id || index}>
                              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mt-3">
                                <div>
                                  <span className="badge text-bg-dark rounded-pill mb-2">
                                    Día {index + 1}
                                  </span>

                                  <h6 className="fw-semibold mb-1">
                                    {formatearFecha(dia.fecha)}
                                  </h6>

                                  <div className="small text-secondary">
                                    {dia.rutaIda || "Sin ruta de ida"}
                                    {" · "}
                                    {dia.transporteIda ||
                                      "Sin transporte"}
                                  </div>
                                </div>

                                <div className="d-flex gap-2 flex-wrap">
                                  <span
                                    className={`badge ${fotos.length > 0
                                      ? "text-bg-success"
                                      : "text-bg-secondary"
                                      }`}
                                  >
                                    <i className="fa-solid fa-camera me-1"></i>
                                    {fotos.length}/
                                    {MAX_FOTOGRAFIAS_DIA}
                                  </span>

                                  <span
                                    className={`badge ${asistencias.length > 0
                                      ? "text-bg-success"
                                      : "text-bg-secondary"
                                      }`}
                                  >
                                    <i className="fa-solid fa-file-signature me-1"></i>
                                    {asistencias.length}/
                                    {MAX_ASISTENCIAS_DIA}
                                  </span>
                                </div>
                              </div>

                              {Array.isArray(dia.actividades) &&
                                dia.actividades.length > 0 && (
                                  <div className="border rounded-3 bg-body-tertiary p-3 mt-3">
                                    <div className="small fw-semibold mb-2">
                                      Actividades programadas
                                    </div>

                                    <div className="d-flex flex-column gap-2">
                                      {dia.actividades.map(
                                        (actividad, actividadIndex) => (
                                          <div
                                            className="small"
                                            key={actividad._id || actividadIndex}
                                          >
                                            <strong>
                                              {formatearHora(
                                                actividad.horaInicio
                                              )}
                                              {" - "}
                                              {formatearHora(
                                                actividad.horaFin
                                              )}
                                            </strong>
                                            {": "}
                                            {actividad.actividad || "—"}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}

                              <div className="row g-3 mt-1 mx-0">
                                {bloquesSoporte.map((bloque) => {
                                  const cantidad = bloque.imagenes.length;
                                  const estaCargando = subiendo === bloque.claveCarga;
                                  const limiteAlcanzado = cantidad >= bloque.maximo;

                                  const cargaDeshabilitada =
                                    !formularioHabilitado ||
                                    limiteAlcanzado ||
                                    estaCargando;

                                  return (
                                    <div
                                      key={bloque.tipo}
                                      className="col-12 col-xl-6 px-0 px-sm-2"
                                      style={{ minWidth: 0 }}
                                    >
                                      <div
                                        className="card h-100 border rounded-4 shadow-sm overflow-hidden"
                                        style={{
                                          minWidth: 0,
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {/* Encabezado */}
                                        <div className="card-header bg-white border-bottom p-3">
                                          <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div
                                              className="d-flex align-items-start gap-2"
                                              style={{ minWidth: 0 }}
                                            >
                                              <div
                                                className="rounded-circle bg-body-tertiary d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{
                                                  width: 38,
                                                  height: 38,
                                                }}
                                              >
                                                <i
                                                  className={`fa-solid ${bloque.tipo === "registroFotografico"
                                                      ? "fa-camera"
                                                      : "fa-file-signature"
                                                    }`}
                                                ></i>
                                              </div>

                                              <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-break">
                                                  {bloque.titulo}
                                                </div>

                                                <small className="text-secondary">
                                                  Máximo {bloque.maximo} imágenes.
                                                </small>
                                              </div>
                                            </div>

                                            <span
                                              className={`badge flex-shrink-0 ${limiteAlcanzado
                                                  ? "text-bg-dark"
                                                  : cantidad > 0
                                                    ? "text-bg-success"
                                                    : "text-bg-secondary"
                                                }`}
                                            >
                                              {cantidad}/{bloque.maximo}
                                            </span>
                                          </div>

                                          <div
                                            className="progress mt-3"
                                            style={{ height: 6 }}
                                          >
                                            <div
                                              className="progress-bar"
                                              role="progressbar"
                                              style={{
                                                width: `${Math.min(
                                                  (cantidad / bloque.maximo) * 100,
                                                  100
                                                )}%`,
                                              }}
                                              aria-valuenow={cantidad}
                                              aria-valuemin={0}
                                              aria-valuemax={bloque.maximo}
                                            />
                                          </div>
                                        </div>

                                        {/* Contenido */}
                                        <div
                                          className="card-body p-3"
                                          style={{
                                            minWidth: 0,
                                            overflow: "hidden",
                                          }}
                                        >
                                          <SelectorArchivos
                                            tipo={bloque.tipo}
                                            diaId={dia._id}
                                            disabled={cargaDeshabilitada}
                                            onUpload={subirArchivos}
                                          />

                                          <div className="alert alert-light border small text-secondary py-2 px-3 mb-3">
                                            <div className="d-flex align-items-start gap-2">
                                              <i className="fa-solid fa-mobile-screen-button mt-1 flex-shrink-0"></i>

                                              <span className="text-break">
                                                {bloque.ayuda}
                                              </span>
                                            </div>
                                          </div>

                                          {limiteAlcanzado && (
                                            <div className="alert alert-secondary small py-2 px-3 mb-3">
                                              <i className="fa-solid fa-circle-check me-2"></i>
                                              Alcanzaste el máximo de imágenes permitido.
                                            </div>
                                          )}

                                          {estaCargando && (
                                            <div className="border rounded-3 bg-body-tertiary text-center py-3 mb-3">
                                              <span className="spinner-border spinner-border-sm me-2"></span>
                                              <span className="small text-secondary">
                                                {bloque.cargando}
                                              </span>
                                            </div>
                                          )}

                                          <div
                                            style={{
                                              width: "100%",
                                              maxWidth: "100%",
                                              minWidth: 0,
                                              overflow: "hidden",
                                            }}
                                          >
                                            {renderGaleria({
                                              imagenes: bloque.imagenes,
                                              tipo: bloque.tipo,
                                              agendaDiaObjectId: dia._id,
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                      )
                    )}

                    <div className="row g-2 justify-content-between mt-3">
                      <div className="col-6 col-md-auto">
                        <button
                          type="button"
                          className="btn btn-outline-dark w-100"
                          onClick={() => setActiveTab("informe")}
                          disabled={loading || guardando}
                          title="Volver al informe"
                        >
                          <i className="fa-solid fa-arrow-left me-2"></i>
                          <span className="d-none d-md-inline">Volver al informe</span>
                          <span className="d-md-none">Informe</span>
                        </button>
                      </div>

                      <div className="col-6 col-md-auto">
                        <button
                          type="submit"
                          className="btn btn-dark w-100"
                          disabled={!formularioHabilitado}
                          title={
                            legalizacionId
                              ? "Actualizar legalización"
                              : "Guardar legalización"
                          }
                        >
                          <i
                            className={`fa-solid ${guardando
                              ? "fa-spinner fa-spin"
                              : "fa-floppy-disk"
                              } me-2`}
                          ></i>
                          <span className="d-none d-md-inline">
                            {legalizacionId
                              ? "Actualizar legalización"
                              : "Guardar legalización"}
                          </span>
                          <span className="d-md-none">
                            {legalizacionId ? "Actualizar" : "Guardar"}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 text-center text-secondary small">
                  Guarda la información general y administra las imágenes
                  de cada día desde la sección de soportes.
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    
  );
};

export default RegistrarLegalizcionAgendaViatico;